<template>
  <section class="container-fluid px-0">
    <div class="row mx-0 py-3 bg-dark card">
      <div class="col-12">
        <form @submit.prevent="handleSubmit" class="needs-validation">
          <p v-if="loadingProduct" role="status">Cargando producto…</p>
          <fieldset :disabled="isSubmitting || loadingProduct" class="border-0 p-0 m-0">
          <!-- Selección de tipo de producto -->
          <div class="card mb-4" v-if="!isEditMode">
            <div class="card-header">
              <h4 class="mb-0 fs-6 py-3">
                <i class="fas fa-question-circle me-2"></i>¿Qué tipo de producto quieres crear?
              </h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-4">
                  <div class="card h-100 product-type-card" :class="{'border border-warning': productType === 'seed'}" @click="selectProductType('seed')">
                    <div class="card-body text-center py-4">
                      <i class="fas fa-seedling fa-3x mb-3 text-primary"></i>
                      <h5>Blíster de Semillas</h5>
                      <p class="mb-0 text-muted">Semillas fotoperiódicas, autoflorecientes o regulares</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <div class="card h-100 product-type-card" :class="{'border border-warning': productType === 'merch'}" @click="selectProductType('merch')">
                    <div class="card-body text-center py-4">
                      <i class="fas fa-tshirt fa-3x mb-3 text-primary"></i>
                      <h5>Merchandising</h5>
                      <p class="mb-0 text-muted">Productos como camisetas, gorras, etc.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        
          <!-- Información Básica -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 fs-6 py-3">
                <i class="fas fa-info-circle me-2"></i>Información Básica
              </h4>
            </div>
            <div class="card-body">
              <div class="row g-3">                
                <div class="col-md-6">
                  <label class="form-label">Nombre del Producto</label>
                  <input v-model="product.name" type="text" class="form-control" placeholder="Nombre del producto" required />
                </div>
                <div class="col-md-6" v-if="isSeedProduct">
                  <label class="form-label">Marca</label>
                  <input v-model="product.brand" type="text" class="form-control" placeholder="Marca del producto" />
                </div>
                
                <!-- Campos específicos para semillas -->
                <template v-if="isSeedProduct">
                  <div class="col-md-6">
                    <label class="form-label">Genotipo</label>
                    <input v-model="product.genotype" type="text" class="form-control" placeholder="Ejemplo: Doble Tangie x Orange Blossom" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Aromas</label>
                    <input v-model="product.flavors" type="text" class="form-control" placeholder="Ejemplo: Cítrico, Terrozo" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Efectos</label>
                    <input v-model="product.effects" type="text" class="form-control" placeholder="Ejemplo: Eufórico, Narcótico" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Satividad (%)</label>
                    <input v-model="product.sativa" type="text" class="form-control" placeholder="Ejemplo: 40%" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Indica (%)</label>
                    <input v-model="product.indica" type="text" class="form-control" placeholder="Ejemplo: 60%" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Tipo</label>
                    <select v-model="product.type" class="form-select">
                      <option value="fem">Fotoperiódica</option>
                      <option value="auto">Autofloreciente</option>
                      <option value="reg">Regular</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Contenido de THC (%)</label>
                    <input v-model="product.thc" type="text" class="form-control" placeholder="Ejemplo: 20% - Alto" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Contenido de CBD (%)</label>
                    <input v-model="product.cbd" type="text" class="form-control" placeholder="Ejemplo: 1% - Bajo" />
                  </div>
                </template>
                
                <!-- Campos específicos para merchandising -->
                <template v-if="isMerchProduct">
                  <div class="col-md-6">
                    <label class="form-label">Talle</label>
                    <input v-model="product.product_size" type="text" class="form-control" placeholder="Ejemplo: S, M, L, XL" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Tienda ingreso</label>
                    <div class="form-check form-switch mb-2">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        v-model="product.visible_in_store"
                        id="ingresoStoreMerchSwitch"
                      />
                      <label class="form-check-label" for="ingresoStoreMerchSwitch">
                        {{ product.visible_in_store ? 'Visible en tienda' : 'No visible en tienda' }}
                      </label>
                    </div>
                  </div>
                </template>
                <div class="col-md-6" v-if="isSeedProduct">
                  <label class="form-label">Opciones de Visualización</label>
                  <div class="form-check form-switch mb-2">
                    <input class="form-check-input" type="checkbox" v-model="product.highlighted" id="highlightedSwitch">
                    <label class="form-check-label" for="highlightedSwitch">
                      {{ product.highlighted ? 'Mostrar en destacados' : 'No destacado' }}
                      <i class="fas fa-info-circle ms-1 text-primary" 
                         data-bs-toggle="tooltip" 
                         title="Los productos destacados se muestran en el carrusel de la página de inicio"></i>
                    </label>
                  </div>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="product.limited_edition" id="limitedEditionSwitch">
                    <label class="form-check-label" for="limitedEditionSwitch">
                      {{ product.limited_edition ? 'Edición limitada' : 'No es edición limitada' }}
                      <i class="fas fa-info-circle ms-1 text-primary" 
                         data-bs-toggle="tooltip" 
                         title="Las ediciones limitadas se pueden filtrar específicamente desde el catálogo"></i>
                    </label>
                  </div>
                  <div class="form-check form-switch mt-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="product.show_on_public_web"
                      id="publicWebSwitch"
                    />
                    <label class="form-check-label" for="publicWebSwitch">
                      {{ product.show_on_public_web ? 'Visible en web pública' : 'Solo catálogo interno / ingreso' }}
                      <i
                        class="fas fa-info-circle ms-1 text-primary"
                        data-bs-toggle="tooltip"
                        title="Si está activo, se crea una ficha en la base solo con datos de exhibición (sin precios ni stock en la web pública). Los precios y la venta siguen en este producto para ingreso y panel."
                      />
                    </label>
                  </div>
                  <p v-if="product.show_on_public_web" class="small text-muted mb-0 mt-2">
                    La web pública mostrará nombre, imágenes, descripción y fichas técnicas. No se publican precios ni variantes de compra.
                  </p>
                  <div class="form-check form-switch mt-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="product.visible_in_store"
                      id="ingresoStoreSwitch"
                    />
                    <label class="form-check-label" for="ingresoStoreSwitch">
                      {{ product.visible_in_store ? 'Visible en tienda' : 'No visible en tienda' }}
                      <i
                        class="fas fa-info-circle ms-1 text-primary"
                        data-bs-toggle="tooltip"
                        title="Al guardar, publica todas las presentaciones del producto en la tienda ingreso (ingreso.kameseeds.com)."
                      />
                    </label>
                  </div>
                  <p v-if="product.visible_in_store" class="small text-muted mb-0 mt-2">
                    Se publicará una entrada por cada presentación con precios y stock del catálogo.
                  </p>
                </div>
                <div class="col-12">
                  <label class="form-label">Descripción</label>
                  <textarea v-model="product.description" class="form-control" rows="3" placeholder="Descripción del producto"></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-image me-2"></i>Imágenes
              </h4>
              <small class="text-muted fs-6">
                <i class="fas fa-info-circle me-1"></i>
                Resolución recomendada: 1000x1000px
              </small>
            </div>
            <div class="card-body" @paste="handlePaste">
              <div class="row g-3">
                <div class="col-md-4" v-for="(slot, index) in imageSlots" :key="slot.key">
                  <div
                    class="image-slot-card"
                    :class="{ 'drag-over': dragIndex !== null && dragIndex !== index, 'dragging': dragIndex === index }"
                    :draggable="!uploadingImages && !isSubmitting"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, $event)"
                    @dragend="onDragEnd"
                  >
                    <div class="image-slot-handle" title="Arrastrá para cambiar el orden">
                      <i class="fas fa-grip-vertical"></i>
                      <span>Arrastrá</span>
                    </div>
                    <label
                      v-if="index === 0"
                      class="form-label fw-semibold image-slot-label"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Esta imagen se muestra en las tarjetas del catálogo"
                    >
                      <i class="fas fa-star me-1 text-warning"></i>Imagen principal
                    </label>
                    <label v-else class="form-label fw-semibold image-slot-label mb-0">Imagen {{ index + 1 }}</label>
                    <div class="image-slot-preview">
                      <span class="image-slot-index" :class="{ 'is-primary': index === 0 }">{{ index + 1 }}</span>
                      <img
                        v-if="slot.preview"
                        :src="slot.preview"
                        class="img-preview rounded"
                        alt="Vista previa del producto"
                        loading="lazy"
                        @error="failedPreviews[slot.url] = true"
                        @load="delete failedPreviews[slot.url]"
                      />
                      <div v-else class="image-slot-empty">
                        <i class="fas fa-image fa-2x mb-1"></i>
                        <span class="small">Sin imagen</span>
                      </div>
                      <button
                        v-if="slot.preview"
                        type="button"
                        class="btn btn-danger btn-sm image-slot-remove"
                        :disabled="uploadingImages || isSubmitting"
                        @click="removeImage(index)"
                        title="Eliminar imagen"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                    <div v-if="failedPreviews[slot.url]" class="small text-warning" role="status">
                      No se pudo mostrar la foto. Esto no significa que la carga haya fallado.
                      <button type="button" class="btn btn-link btn-sm" @click="retryPreview(slot.url)">Reintentar vista previa</button>
                    </div>
                    <div class="image-slot-arrows">
                      <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="index === 0 || uploadingImages || isSubmitting"
                        @click="moveImage(index, -1)"
                        title="Mover a la izquierda (más cerca de la principal)"
                      >
                        <i class="fas fa-chevron-left"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="index === imageSlots.length - 1 || uploadingImages || isSubmitting"
                        @click="moveImage(index, 1)"
                        title="Mover a la derecha"
                      >
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm w-100 mt-2 image-slot-upload"
                      :disabled="uploadingImages || isSubmitting"
                      @click="openFilePicker(index)"
                    >
                      <i class="fas fa-upload me-1"></i>Reemplazar
                    </button>
                  </div>
                </div>
              </div>
              <button type="button" class="btn btn-outline-primary mt-3" :disabled="uploadingImages || isSubmitting" @click="openAddImagePicker">
                <i class="fas fa-plus me-1"></i>{{ uploadingImages ? 'Subiendo fotos…' : 'Agregar fotos' }}
              </button>
              <input ref="addImageInput" type="file" class="d-none" multiple accept="image/jpeg,image/png,image/webp,image/gif,image/avif" @change="handleUploadImages($event)" />
              <input ref="replaceImageInput" type="file" class="d-none" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" @change="handleReplaceImage" />
              <p class="small text-muted mt-2 mb-0">Sin límite de cantidad · Hasta 20 MB por foto · JPG, PNG, WebP, GIF o AVIF.</p>
              <p v-if="imageUploadStatus" class="small mt-2" role="status">{{ imageUploadStatus }}</p>
              <div v-if="imageUploadErrors.length" class="alert alert-danger mt-2" role="alert">
                <strong>Estas fotos no se agregaron:</strong>
                <ul class="mb-0"><li v-for="(message, i) in imageUploadErrors" :key="i">{{ message }}</li></ul>
                Las fotos cargadas correctamente se conservan. Podés volver a seleccionar las que fallaron.
              </div>
              <p class="small text-muted mb-0 mt-3">
                <i class="fas fa-grip-vertical me-1"></i>
                Arrastrá las imágenes o usá las flechas para cambiar el orden. La <strong>Imagen principal</strong> (primera) es la que se muestra en las tarjetas del catálogo.
              </p>
            </div>
          </div>

          <VideoUploadField v-model="product.videos" class="mb-4" label="Videos del producto" :max-videos="3" :disabled="isSubmitting" @busy="uploadingVideo = $event" />

          <!-- Información de Crecimiento (solo para semillas) -->
          <div class="card mb-4" v-if="isSeedProduct">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-seedling me-2"></i>Información de Crecimiento
              </h4>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Tiempo de Floración</label>
                  <input v-model="product.flowering_time" type="text" class="form-control" placeholder="Ejemplo: 70 Días / 7 Semanas" />
                </div>
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Ciclo Completo</label>
                  <input v-model="product.completed_cicle" type="text" class="form-control" placeholder="Ejemplo: 70 Días / 7 Semanas" />
                </div>

                <!-- Información de Rendimiento -->
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Rendimiento Interior</label>
                  <input v-model="product.yield_indoor" type="text" class="form-control" placeholder="Ejemplo: 500 g/m²" />
                </div>
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Rendimiento Exterior</label>
                  <input v-model="product.yield_outdoor" type="text" class="form-control" placeholder="Ejemplo: 600 g/planta" />
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-truck me-2"></i>Datos de envío (Correo Argentino)
              </h4>
              <small class="text-muted">
                Por unidad / presentación. Si dejás vacío, al cotizar se usan los valores por defecto del panel de envíos.
              </small>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Peso (gramos)</label>
                  <input v-model.number="product.weight_grams" type="number" min="1" max="25000" class="form-control" placeholder="Ej: 500" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Largo (cm)</label>
                  <input v-model.number="product.length_cm" type="number" min="1" max="150" class="form-control" placeholder="Ej: 20" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Ancho (cm)</label>
                  <input v-model.number="product.width_cm" type="number" min="1" max="150" class="form-control" placeholder="Ej: 15" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Alto (cm)</label>
                  <input v-model.number="product.height_cm" type="number" min="1" max="150" class="form-control" placeholder="Ej: 10" />
                </div>
              </div>
            </div>
          </div>

          <!-- Precios, presentaciones y roles -->
          <ProductPricingSection
            v-if="isSeedProduct || isMerchProduct"
            :is-merch="isMerchProduct"
            v-model:blisters="product.blisters"
          />

          <!-- Metadatos -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5"><i class="fas fa-tags me-2"></i>Metadatos</h4>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12">
                  <label class="form-label">SKU</label>
                  <input v-model="product.sku" type="text" class="form-control" placeholder="Ejemplo: GG-001" />
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting || uploadingVideo || uploadingImages">
              <i class="fas fa-save me-2"></i>
              {{ isSubmitting ? "Guardando..." : (isEditMode ? "Actualizar Producto" : "Guardar Producto") }}
            </button>
            <button type="button" class="btn btn-secondary" :disabled="uploadingImages || uploadingVideo" @click="resetForm">
              <i class="fas fa-undo me-2"></i>Reiniciar
            </button>
          </div>
          <p v-if="saveError" class="alert alert-danger mt-3" role="alert">{{ saveError }}</p>
          </fieldset>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getProduct, createProduct, updateProduct, uploadImage } from "@/services/products";
import { freshAdminImage } from '@/utils/adminFreshness';
import VideoUploadField from "@/components/media/VideoUploadField.vue";
import ProductPricingSection from "@/components/admin/ProductPricingSection.vue";
import { useUnsavedChangesGuard, useFormDirty } from "@/composables/useUnsavedChangesGuard";
// toastification
import { useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";

// Configuración de toastification
const toast = useToast();

// Tipo de producto que se está creando (seed o merch)
const productType = ref('seed');

// Estructura inicial del producto para semillas
const initialSeedProduct = {
  name: "",
  type: "fem",
  genotype: "",
  sativa: "",
  indica: "",
  highlighted: false,
  limited_edition: false,
  show_on_public_web: false,
  visible_in_store: false,
  product_size: "",
  description: "",
  flowering_time: "",
  completed_cicle: "",
  height_indoor: "",
  height_outdoor: "",
  yield_indoor: "",
  yield_outdoor: "",
  thc: "",
  cbd: "",
  seed_count: 0,
  price: 0,
  stock: 0,
  sku: "",
  image: null,
  image_2: null,
  image_3: null,
  images: [],
  videos: [],
  weight_grams: null,
  length_cm: null,
  width_cm: null,
  height_cm: null,
  blisters: []
};

// Estructura inicial del producto para merch
const initialMerchProduct = {
  name: "",
  type: "merch",
  brand: "",
  highlighted: false,
  limited_edition: false,
  show_on_public_web: false,
  visible_in_store: false,
  description: "",
  product_size: "",
  price: 0,
  stock: 0,
  discount: 0,
  sku: "",
  image: null,
  image_2: null,
  image_3: null,
  images: [],
  videos: [],
  weight_grams: null,
  length_cm: null,
  width_cm: null,
  height_cm: null,
  blisters: []
};

// Producto inicial basado en el tipo seleccionado
const initialProduct = initialSeedProduct;

// Computed properties para determinar el tipo de producto
const isSeedProduct = computed(() => {
  return isEditMode.value ? product.value.type !== 'merch' : productType.value === 'seed';
});

const isMerchProduct = computed(() => {
  return isEditMode.value ? product.value.type === 'merch' : productType.value === 'merch';
});


const product = ref({ ...initialProduct });
const productDirty = useFormDirty(product, { initial: initialSeedProduct });
const uploadingVideo = ref(false);
const loadingProduct = ref(true);
const saveError = ref('');
const imageUploadErrors = ref([]);
const imageUploadStatus = ref('');
const failedPreviews = ref({});
const previewRetries = ref({});
let uploadController = null;
let disposed = false;
onBeforeUnmount(() => { disposed = true; uploadController?.abort(); });
const uploadingImages = ref(false);
const addImageInput = ref(null);
const replaceImageInput = ref(null);
const replaceImageIndex = ref(null);
const imageSlots = computed(() => (product.value.images || []).map((url, index) => ({
  key: `${index}-${url}`, url, preview: previewRetries.value[url] || freshAdminImage(url, product.value),
})));
const retryPreview = (url) => {
  const preview = new URL(url, window.location.origin);
  preview.searchParams.set('_preview_retry', Date.now().toString());
  previewRetries.value[url] = preview.toString();
};
const openFilePicker = (index) => {
  replaceImageIndex.value = index;
  replaceImageInput.value?.click();
};
const openAddImagePicker = () => addImageInput.value?.click();
const handleReplaceImage = (event) => handleUploadImages(event, replaceImageIndex.value);

// --- Reordenamiento de imágenes (drag & drop + flechas) ---
// El orden define la imagen de catálogo: la PRIMERA (image) es la que se ve en los cards.
const dragIndex = ref(null);

const onDragStart = (index) => {
  dragIndex.value = index;
};

const onDragOver = (event) => {
  event.preventDefault();
};

const onDrop = (index, event) => {
  event.preventDefault();
  const from = dragIndex.value;
  dragIndex.value = null;
  if (from === null || from === index || uploadingImages.value || isSubmitting.value) return;
  reorderImages(from, index);
};

const onDragEnd = () => {
  dragIndex.value = null;
};

const moveImage = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= imageSlots.value.length) return;
  reorderImages(index, target);
};

const reorderImages = (from, to) => {
  const images = [...product.value.images];
  const [moved] = images.splice(from, 1);
  images.splice(to, 0, moved);
  product.value.images = images;
};

const isSubmitting = ref(false);
const isEditMode = ref(false);
const route = useRoute();
const router = useRouter();
const productId = route.params.productId;
const applyProductData = (data) => {
  product.value = {
    ...data,
    images: Array.isArray(data.images) ? [...data.images] : [data.image, data.image_2, data.image_3].filter(Boolean),
    videos: data.videos || [],
    blisters: data.blisters || [],
    show_on_public_web: Boolean(data.show_on_public_web),
    visible_in_store: Boolean(data.visible_in_store),
  };
  productDirty.markClean();
};

onMounted(async () => {  
  try {
    console.log('ProductForm mounted, productId:', productId);
    
    // Initialize Bootstrap tooltips
    setTimeout(() => {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }, 500);
    
    if (productId) {
      console.log('Edit mode - loading product with ID:', productId);
      isEditMode.value = true;
      
      try {
        const productData = await getProduct(productId);
        console.log('Product data loaded:', productData);
        
        // If limited_edition field doesn't exist in the loaded data, initialize it
        if (productData.limited_edition === undefined) {
          productData.limited_edition = false;
        }
        productData.show_on_public_web = Boolean(productData.show_on_public_web);
        productData.visible_in_store = Boolean(productData.visible_in_store);

        if (!disposed) applyProductData(productData);
      } catch (productError) {
        if (disposed) return;
        console.error('Error loading specific product:', productError);
        toast.error(`Error al cargar el producto: ${productError.message}`);
        // Redirigir a la lista de productos si no se puede cargar el producto
        router.push({ name: 'AdminProductList' });
        return;
      }
    } else {
      console.log('Create mode - no productId provided');
    }
  } catch (error) {
    console.error("Error al cargar datos iniciales:", error);
    toast.error(`Error al cargar los datos iniciales: ${error.message}`);
  } finally {
    loadingProduct.value = false;
  }
});

const handleUploadImages = async (event, replaceIndex = null) => {
  const files = Array.from(event.target.files || []);
  if (event.target.value !== undefined) event.target.value = '';
  if (!files.length || uploadingImages.value || isSubmitting.value || loadingProduct.value || disposed) return;
  uploadingImages.value = true;
  imageUploadErrors.value = [];
  uploadController = new AbortController();
  let uploaded = 0;
  const selected = replaceIndex === null ? files : files.slice(0, 1);
  try {
    for (const [index, file] of selected.entries()) {
      if (disposed) break;
      imageUploadStatus.value = `Subiendo ${index + 1} de ${selected.length}: ${file.name}`;
      try {
        const url = await uploadImage(file, {
          signal: uploadController.signal,
          onUploadProgress: ({ loaded, total }) => {
            if (!disposed && total) imageUploadStatus.value = `${file.name}: ${Math.round(loaded / total * 100)}% · ${loaded >= total ? 'Validando y guardando la foto…' : 'Subiendo…'}`;
          },
        });
        if (disposed) break;
        const images = [...product.value.images];
        if (replaceIndex === null) images.push(url);
        else images[replaceIndex] = url;
        product.value.images = images;
        uploaded += 1;
      } catch (error) {
        if (disposed || error.code === 'ERR_CANCELED') break;
        imageUploadErrors.value.push(`${file.name}: ${error.message}`);
      }
    }
  } finally {
    imageUploadStatus.value = uploaded ? `${uploaded} foto(s) cargada(s). Guardá el producto para publicar los cambios.` : '';
    uploadingImages.value = false;
    uploadController = null;
  }
};

const handlePaste = async (event) => {
  const files = Array.from(event.clipboardData.items || [])
    .filter(item => item.type.startsWith('image/'))
    .map(item => item.getAsFile()).filter(Boolean);
  if (files.length) {
    event.preventDefault();
    await handleUploadImages({ target: { files } });
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value || loadingProduct.value) return;
  if (uploadingVideo.value || uploadingImages.value) { toast.warning("Terminá o cancelá la carga antes de guardar."); return; }
  saveError.value = '';
  isSubmitting.value = true;
  try {
    if (product.value.visible_in_store) {
      const blisters = product.value.blisters || [];
      if (!blisters.length) {
        toast.error('Agregá al menos una presentación para publicar en la tienda');
        return;
      }
    }

    let productToSend;
    
    if (isSeedProduct.value) {
      productToSend = {
        ...product.value,
        type: product.value.type || 'fem',
        show_on_public_web: Boolean(product.value.show_on_public_web),
        visible_in_store: Boolean(product.value.visible_in_store),
        blisters: product.value.blisters.map(blister => ({
          ...(blister.id ? { id: blister.id } : {}),
          seed_count: Number(blister.seed_count),
          price: Number(blister.price),
          stock: Number(blister.stock),
          discount: Number(blister.discount || 0),
          min_order_quantity: Number(blister.min_order_quantity || 1),
          role_prices: blister.role_prices || {},
        })),
      };
    } else {
      const blisters = product.value.blisters?.length
        ? product.value.blisters
        : [{
            price: product.value.price,
            stock: product.value.stock,
            discount: product.value.discount || 0,
            seed_count: 1,
          }];
      productToSend = {
        ...product.value,
        type: 'merch',
        show_on_public_web: Boolean(product.value.show_on_public_web),
        visible_in_store: Boolean(product.value.visible_in_store),
        blisters: blisters.map(blister => ({
          ...(blister.id ? { id: blister.id } : {}),
          seed_count: 1,
          price: Number(blister.price),
          stock: Number(blister.stock),
          discount: Number(blister.discount || 0),
          min_order_quantity: Number(blister.min_order_quantity || 1),
          role_prices: blister.role_prices || {},
        })),
      };
    }

    console.log('Submitting product:', productToSend);

    if (isEditMode.value) {
      const saved = await updateProduct(productId, productToSend);
      applyProductData(saved);
      toast.success("Producto actualizado");
    } else {
      const saved = await createProduct(productToSend);
      applyProductData(saved);
      toast.success("Producto guardado");
      router.push({ name: 'AdminProductList' }); // Solo redirige en caso de crear nuevo
    }
    imageUploadStatus.value = '';
    productDirty.markClean();
  } catch (error) {
    console.error(error);
    saveError.value = error.message;
    toast.error(error.message);
  } finally {
    isSubmitting.value = false;
  }
};

useUnsavedChangesGuard({
  dirty: computed(() => productDirty.dirty.value || uploadingVideo.value || uploadingImages.value),
  saveFn: () => handleSubmit(),
  message: 'Tenés cambios sin guardar en el formulario del producto. Si salís ahora, se van a perder.',
});

const removeImage = (index) => {
  product.value.images = product.value.images.filter((_, i) => i !== index);
};

const selectProductType = (type) => {
  if (uploadingImages.value || uploadingVideo.value || isSubmitting.value || loadingProduct.value) return;
  productType.value = type;
  
  // Resetear el formulario con el tipo apropiado
  if (type === 'seed') {
    product.value = { 
      ...initialSeedProduct,
      blisters: []
    };
  } else {
    product.value = {
      ...initialMerchProduct
    };
  }
  
  if (addImageInput.value) addImageInput.value.value = '';
  if (replaceImageInput.value) replaceImageInput.value.value = '';
};

const resetForm = () => {
  if (uploadingImages.value || uploadingVideo.value || isSubmitting.value || loadingProduct.value) return;
  if (isSeedProduct.value) {
    product.value = { 
      ...initialSeedProduct,
      blisters: []
    };
  } else {
    product.value = {
      ...initialMerchProduct
    };
  }
  
  if (addImageInput.value) addImageInput.value.value = '';
  if (replaceImageInput.value) replaceImageInput.value.value = '';
};


</script>

<style scoped>
section {
  min-height: auto;
  padding-bottom: 50px;
}
.card {
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  background: var(--panel-surface);
  color: var(--white-color);
}

.card-header {
  border-bottom: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.12);
}

input, textarea,
.form-select {
  background-color: rgba(0, 0, 0, 0.25);
  color: var(--white-color);
  border: 1px solid var(--panel-border);
}

select {
  background-color: rgba(0, 0, 0, 0.25);
  color: var(--white-color);
}

option {
  background-color: var(--grey-color);
  color: var(--white-color);
}


.img-preview {
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
}

/* Slots de imagen (preview + handle drag + flechas + botón subir) */
.image-slot-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent 45%), var(--black-color-2);
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease;
}

.image-slot-card:hover {
  border-color: rgba(255, 255, 255, 0.22);
}

.image-slot-card.drag-over {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.35);
}

.image-slot-card.dragging {
  opacity: 0.45;
  transform: scale(0.98);
}

.image-slot-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.3rem 0;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.5);
  cursor: grab;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  flex-shrink: 0;
  user-select: none;
}

.image-slot-handle:hover {
  color: var(--primary-color);
  background: rgba(255, 255, 255, 0.09);
}

.image-slot-handle:active {
  cursor: grabbing;
}

.image-slot-label {
  min-height: 1.6rem; /* igual altura en los 3 slots */
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.88rem;
}

.image-slot-preview {
  position: relative;
  height: 170px; /* tamaño fijo e igual en los 3 contenedores */
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.35));
  border-radius: 8px;
  overflow: hidden;
  padding: 0.5rem;
}

.image-slot-index {
  position: absolute;
  top: 0.4rem;
  left: 0.4rem;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.7rem;
  font-weight: 700;
  z-index: 2;
}

.image-slot-index.is-primary {
  background: var(--primary-color);
  color: #fff;
}

.image-slot-remove {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  z-index: 2;
}

.image-slot-preview .img-preview {
  max-width: 100%;
  max-height: 100%;
}

.image-slot-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.35);
  min-height: 100px;
}

.image-slot-arrows {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.6rem;
}

.image-slot-arrows .btn {
  border-radius: 6px;
  padding: 0.2rem 0.7rem;
  min-width: 2.2rem;
  margin-bottom: 0.6rem;
}

.image-slot-upload {
  margin-top: auto !important;
  border-radius: 6px;
  font-weight: 500;
}

.blister-item {
  background: var(--black-color-2);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.blister-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.blisters-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.blisters-list::-webkit-scrollbar {
  width: 8px;
}

.blisters-list::-webkit-scrollbar-track {
  background: var(--black-color-2);
  border-radius: 4px;
}

.blisters-list::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: 4px;
}

.price-preview {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  background: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
}

.card-header small {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

/* Estilos para el dropdown de marcas */
.position-relative {
  position: relative;
}

/* Estilos para la selección de tipo de producto */
.product-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.product-type-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.product-type-card.border-primary {
  border-color: var(--primary-color);
  box-shadow: 0 0 15px rgba(var(--primary-color-rgb), 0.3);
}
</style>
